/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { CalendarBlank, ArrowRight, BookOpen } from "@phosphor-icons/react";

// Public /blog list. Pulls the merged feed (local posts + rankai.ai
// articles) from GET /api/blog. Designed for SEO — server-side
// renderable enough that AI crawlers and rankai's own indexer can pick
// up structure (h1 list, anchor tags with descriptive text).

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/blog?page=${page}&limit=12`)
      .then((res) => {
        if (!alive) return;
        setPosts(res.data?.posts || []);
        setTotalPages(res.data?.totalPages || 1);
      })
      .catch(() => {
        if (!alive) return;
        setPosts([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [page]);

  // JSON-LD ItemList — helps Google + AI crawlers see the blog index
  // as a structured collection rather than a generic page.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "AstroKuberChat Blog",
    description:
      "Vedic astrology, chat-with-astrologer guides, and deep tool walkthroughs from the AstroKuberChat team.",
    url: "https://astrokuberchat.com/blog",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `https://astrokuberchat.com/blog/${p.slug}`,
      datePublished: p.published_at,
      image: p.featured_image_url || undefined,
      author: { "@type": "Organization", name: p.author_name || "AstroKuberChat" },
    })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFBF5]" data-testid="blog-list-page">
      <Helmet>
        <title>Astrology Blog · Chat-with-Astrologer Guides & Vedic Deep-Dives — AstroKuberChat</title>
        <meta
          name="description"
          content="Practical guides on talking to a real Vedic astrologer online, plus deep walk-throughs on Dashas, Navamsa, Nakshatras and the tools that power AstroKuberChat."
        />
        <link rel="canonical" href="https://astrokuberchat.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AstroKuberChat Blog — Vedic Astrology Guides" />
        <meta property="og:url" content="https://astrokuberchat.com/blog" />
        <meta property="og:description" content="Chat-with-astrologer guides, Dasha deep-dives, and the tools that power AstroKuberChat." />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 w-full">
        <header className="mb-10 sm:mb-14" data-testid="blog-list-hero">
          <div className="inline-flex items-center gap-2 text-amber-700 text-xs font-medium uppercase tracking-[0.18em] mb-3">
            <BookOpen size={14} weight="duotone" /> AstroKuberChat Journal
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3F2D1B] leading-tight">
            Guides for the curious sky-watcher.
          </h1>
          <p className="mt-4 max-w-2xl text-stone-600 text-base sm:text-lg leading-relaxed">
            Practical, math-honest writing about Vedic astrology — how to chat with an astrologer
            without getting sold to, how Dashas actually time events, and what each of our tools
            is really measuring.
          </p>
        </header>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list-loading">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-2xl bg-stone-100 animate-pulse h-72" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-stone-500" data-testid="blog-list-empty">
            <BookOpen size={42} weight="duotone" className="mx-auto mb-4 text-stone-300" />
            <p className="text-base">No posts yet. Check back soon — we're writing.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list-grid">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group rounded-2xl overflow-hidden bg-white border border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all flex flex-col"
              >
                {p.featured_image_url ? (
                  <div className="aspect-[16/9] overflow-hidden bg-amber-50">
                    <img
                      src={p.featured_image_url}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                    <BookOpen size={36} weight="duotone" className="text-amber-700/60" />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[11px] text-stone-500 mb-2">
                    <CalendarBlank size={13} />
                    <time dateTime={p.published_at}>{formatDate(p.published_at)}</time>
                    {p.source === "rankai" ? (
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-600">SEO</span>
                    ) : null}
                  </div>
                  <h2 className="font-serif-display text-xl text-[#3F2D1B] leading-snug group-hover:text-amber-800 transition-colors">
                    {p.title}
                  </h2>
                  {p.excerpt ? (
                    <p className="mt-2 text-sm text-stone-600 line-clamp-3">{p.excerpt}</p>
                  ) : null}
                  <span className="mt-auto pt-4 inline-flex items-center gap-1 text-sm font-medium text-amber-700 group-hover:gap-2 transition-all">
                    Read post <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {totalPages > 1 ? (
          <div className="mt-10 flex items-center justify-center gap-2" data-testid="blog-pagination">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 rounded-full border border-amber-200 text-sm text-stone-700 disabled:opacity-40 hover:bg-amber-50"
              data-testid="blog-prev-page"
            >
              ← Prev
            </button>
            <span className="text-sm text-stone-500 px-3">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-4 py-2 rounded-full border border-amber-200 text-sm text-stone-700 disabled:opacity-40 hover:bg-amber-50"
              data-testid="blog-next-page"
            >
              Next →
            </button>
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
